import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    clientsCompanyResource,
    plansResource,
} from "../../../helpers/configRoutes/backend_helper";
import { currencyUSD } from "../../../helpers/forms/formater";
import ProfileAccountForm from "./ProfileAccountForm";

const ProfileAccounting = () => {
    const navigate = useNavigate();
    const userStore = useSelector((state) => state.Login.user) || {};

    const [company, setCompany] = useState(userStore.company || {});
    const [plans, setPlans] = useState([]);

    const [modal, setModal] = useState({
        show: false,
        title: "Edit profile",
        profile: true, // true => company/profile, false => billing/payment
    });

    // Helpers
    const getInitials = (name = "") =>
        name
            .split(" ")
            .filter(Boolean)
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();

    // Cargar company fresca
    useEffect(() => {
        const fetchCompany = async () => {
            if (!userStore.company_id) return;
            const response = await clientsCompanyResource(
                "show",
                {},
                userStore.company_id
            );
            if (response.status && response.company) {
                setCompany(response.company);
            }
        };
        fetchCompany();
    }, [userStore.company_id]);

    // Cargar planes para contexto de plan actual
    useEffect(() => {
        const fetchPlans = async () => {
            const response = await plansResource("get", {
                params: { perPage: 50, page: 1 },
            });
            if (response.status && response.plans) {
                setPlans(response.plans);
            }
        };
        fetchPlans();
    }, []);

    const currentPlan =
        plans.find((p) => p.id === company.plan_id) ||
        plans.find((p) => p.id === userStore.plan_id) ||
        null;

    const planName =
        currentPlan?.name ||
        company.plan_name ||
        userStore.plan_name ||
        "No active plan";
    const usersCount =
        company.users_count || userStore.users_count || currentPlan?.users || 1;
    const trucksLimit = currentPlan?.trucks ?? null;
    const price = currentPlan ? Number(currentPlan.price || 0) : 0;
    const taxes = currentPlan
        ? Number(
              price === 0
                  ? 0
                  : (price * (Number(currentPlan.taxes || 0) / 100))
          )
        : 0;

    const preferredMethod =
        (userStore.billing && userStore.billing.preferred_method) ||
        "Not configured";

    const openProfileModal = () =>
        setModal({
            show: true,
            title: "Edit company & contact details",
            profile: true,
        });

    const openBillingModal = () =>
        setModal({
            show: true,
            title: "Billing & invoicing settings",
            profile: false,
        });

    return (
        <div className="min-h-screen bg-cs-bg-alt py-8 px-4">
            <div className="max-w-auto mx-auto space-y-6">
                {/* HEADER */}
                <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <div>
                        <p className="text-xs uppercase tracking-[0.18em] text-cs-muted">
                            Account center
                        </p>
                        <h1 className="text-2xl font-semibold text-cs-dark">
                            Account & Billing
                        </h1>
                        <p className="text-sm text-cs-muted mt-1">
                            Manage your profile, company information, subscription and payment methods from a single place.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 justify-start md:justify-end">
                        <span className="px-2 py-1 rounded-full bg-cs-primary-soft text-cs-primary-dark text-xs">
                            {planName}
                        </span>
                        <span className="px-2 py-1 rounded-full bg-cs-bg text-cs-muted text-xs border border-cs-border">
                            {usersCount} active user{usersCount !== 1 ? "s" : ""}
                        </span>
                        {trucksLimit !== null && (
                            <span className="px-2 py-1 rounded-full bg-cs-bg-alt text-cs-muted text-xs border border-cs-border">
                                Up to {trucksLimit} trucks
                            </span>
                        )}
                    </div>
                </header>

                {/* TOP: USER SUMMARY */}
                <section className="bg-cs-bg border border-cs-border rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center gap-4 shadow-sm">
                    {/* Avatar */}
                    <div className="w-14 h-14 rounded-2xl bg-cs-primary-soft flex items-center justify-center text-cs-primary-dark font-semibold text-lg">
                        {getInitials(
                            userStore.name || userStore.email || "CS"
                        )}
                    </div>

                    {/* Info */}
                    <div className="flex-1">
                        <h2 className="text-sm font-semibold text-cs-dark">
                            {userStore.name || "User account"}
                        </h2>
                        <p className="text-xs text-cs-muted">
                            {userStore.email || "No email registered"}
                        </p>
                        <p className="text-xs text-cs-muted mt-1">
                            Linked to:{" "}
                            <span className="font-medium text-cs-primary-dark">
                                {company.name || "No company profile completed yet"}
                            </span>
                        </p>
                    </div>

                    {/* Quick actions */}
                    <div className="flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={openProfileModal}
                            className="
                                px-3 py-1.5 rounded-xl
                                bg-cs-primary
                                text-white text-xs font-medium
                                hover:bg-cs-primary-hover
                                transition-all shadow-sm hover:shadow-md
                                inline-flex items-center gap-1
                            "
                        >
                            <span className="material-symbols-outlined text-sm">
                                edit
                            </span>
                            Edit profile
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate("/team")}
                            className="
                                px-3 py-1.5 rounded-xl
                                bg-cs-bg-alt
                                text-cs-muted text-xs
                                hover:bg-cs-primary-soft hover:text-cs-primary
                                border border-cs-border
                                transition-all
                            "
                        >
                            Manage users & licenses
                        </button>
                    </div>
                </section>

                {/* GRID: COMPANY + BILLING/SUBSCRIPTION */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* COMPANY CARD */}
                    <section className="bg-cs-bg border border-cs-border rounded-2xl p-4 shadow-sm flex flex-col gap-2">
                        <div className="flex items-center justify-between gap-2 mb-1">
                            <div>
                                <h3 className="text-sm font-semibold text-cs-primary-dark">
                                    Company details
                                </h3>
                                <p className="text-xs text-cs-muted">
                                    Legal & contact info used for your account, invoices and operations.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={openProfileModal}
                                className="
                                    px-2 py-1 rounded-xl
                                    bg-cs-bg-alt
                                    text-cs-primary text-xs
                                    hover:bg-cs-primary-soft
                                    hover:text-cs-primary-dark
                                    border border-cs-border
                                    transition-all
                                    inline-flex items-center gap-1
                                "
                            >
                                <span className="material-symbols-outlined text-sm">
                                    tune
                                </span>
                                Edit
                            </button>
                        </div>

                        <div className="mt-1 space-y-1.5 text-xs text-cs-muted">
                            <div>
                                <span className="font-medium text-cs-dark">
                                    Company:{" "}
                                </span>
                                {company.name || "Not set"}
                            </div>
                            <div>
                                <span className="font-medium text-cs-dark">
                                    Contact:{" "}
                                </span>
                                {company.contact_name || "Not set"}
                            </div>
                            <div>
                                <span className="font-medium text-cs-dark">
                                    Phone:{" "}
                                </span>
                                {company.phone
                                    ? `${company.prefix_code
                                          ? `+${company.prefix_code} `
                                          : ""}${company.phone}`
                                    : "Not set"}
                            </div>
                            <div>
                                <span className="font-medium text-cs-dark">
                                    Address:{" "}
                                </span>
                                {company.address || "Not set"}
                            </div>
                            <div>
                                <span className="font-medium text-cs-dark">
                                    Invoicing address:{" "}
                                </span>
                                {company.invoicing_address || "Not set"}
                            </div>

                            <div className="flex flex-wrap gap-2 mt-1">
                                {company.dot && (
                                    <span className="px-2 py-0.5 rounded-full bg-cs-bg-alt text-xs text-cs-muted border border-cs-border">
                                        DOT: {company.dot}
                                    </span>
                                )}
                                {company.mc_number && (
                                    <span className="px-2 py-0.5 rounded-full bg-cs-bg-alt text-xs text-cs-muted border border-cs-border">
                                        MC: {company.mc_number}
                                    </span>
                                )}
                            </div>
                        </div>
                    </section>

                    {/* BILLING + SUBSCRIPTION CARD */}
                    <section className="bg-cs-bg border border-cs-border rounded-2xl p-4 shadow-sm flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-2">
                            <div>
                                <h3 className="text-sm font-semibold text-cs-primary-dark">
                                    Billing, subscription & payments
                                </h3>
                                <p className="text-xs text-cs-muted">
                                    Overview of your current plan, billing contact and preferred payment method.
                                </p>
                            </div>
                        </div>

                        {/* Current plan summary */}
                        <div className="mt-1 grid grid-cols-2 gap-2 text-xs text-cs-muted">
                            <div className="space-y-0.5">
                                <p>
                                    <span className="font-medium text-cs-dark">
                                        Current plan:{" "}
                                    </span>
                                    {planName}
                                </p>
                                {currentPlan && (
                                    <>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Base price:{" "}
                                            </span>
                                            {currencyUSD(price)} / month
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Taxes:{" "}
                                            </span>
                                            {currencyUSD(taxes)}
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="space-y-0.5">
                                {currentPlan && (
                                    <>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Included trucks:{" "}
                                            </span>
                                            {currentPlan.trucks}
                                        </p>
                                        <p>
                                            <span className="font-medium text-cs-dark">
                                                Included users:{" "}
                                            </span>
                                            {currentPlan.users}
                                        </p>
                                    </>
                                )}
                                <p>
                                    <span className="font-medium text-cs-dark">
                                        Billing email:{" "}
                                    </span>
                                    {company.email ||
                                        userStore.email ||
                                        "Not set"}
                                </p>
                            </div>
                        </div>

                        {/* Preferred method */}
                        <div className="text-xs text-cs-muted">
                            <span className="font-medium text-cs-dark">
                                Preferred payment method:{" "}
                            </span>
                            {preferredMethod}
                        </div>

                        {/* Actions */}
                        <div className="mt-2 flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => navigate("/subscriptions")}
                                className="
                                    px-3 py-1.5 rounded-xl
                                    bg-cs-primary-soft
                                    text-cs-primary-dark text-xs
                                    hover:bg-cs-primary
                                    hover:text-white
                                    transition-all
                                    inline-flex items-center gap-1
                                "
                            >
                                <span className="material-symbols-outlined text-sm">
                                    workspace_premium
                                </span>
                                Manage subscription
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate("/accounting")}
                                className="
                                    px-3 py-1.5 rounded-xl
                                    bg-cs-bg-alt
                                    text-cs-muted text-xs
                                    hover:bg-cs-primary-soft
                                    hover:text-cs-primary
                                    border border-cs-border
                                    transition-all
                                    inline-flex items-center gap-1
                                "
                            >
                                <span className="material-symbols-outlined text-sm">
                                    receipt_long
                                </span>
                                View invoices
                            </button>
                            {console.log(userStore)}
                            {userStore.can.CompanyPaymentMethod.view &&
                            <button
                                type="button"
                                onClick={() => navigate("/payment-methods")}
                                className="
                                    px-3 py-1.5 rounded-xl
                                    bg-cs-bg-alt
                                    text-cs-muted text-xs
                                    hover:bg-cs-primary-soft
                                    hover:text-cs-primary
                                    border border-cs-border
                                    transition-all
                                    inline-flex items-center gap-1
                                "
                            >
                                <span className="material-symbols-outlined text-sm">
                                    account_balance
                                </span>
                                Manage methods
                            </button>
                            }
                        </div>
                    </section>
                </div>
            </div>

            {/* Modal existente, misma lógica */}
            <ProfileAccountForm modal={modal} setModal={setModal} />
        </div>
    );
};

export default ProfileAccounting;
